import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {isTrialSampleInterfaceEnabled as baseIsTrialSampleInterfaceEnabled} from 'registry/trial-interfaces/isTrialSampleInterfaceEnabled';

import {
  SamplesAllowedAssayTemplateQuery_bioentities as BioentityOrInaccessibleInfo,
  SamplesAllowedAssayTemplateQuery_bioentities_BasicFolderItemNode as BioentityWithTrialSampleInterface,
  SamplesAllowedAssayTemplateQuery_bioentities_BasicFolderItemNode_sampleDefinitionLinks_SampleDefinitionLinkNode as SampleDefinitionLinkNode,
} from './__generated/SamplesAllowedAssayTemplateQuery';
import {StudyNode, ProcedureDropdownTemplate, AccessibleBioentity} from './SampleAssayTemplateSelectionTable';

/**
 * Checks if the trial sample system fieldset is enabled for a given entity.
 *
 * See baseIsTrialSampleInterfaceEnabled for full logic.
 *
 * @param entityOrInaccessibleInfo The GraphQL node representing the entity or inaccessible info to evaluate.
 * @returns {boolean} True if the entity is a trial sample, false otherwise.
 */
export function isTrialSampleInterfaceEnabled(
  entityOrInaccessibleInfo: BioentityOrInaccessibleInfo
): entityOrInaccessibleInfo is BioentityWithTrialSampleInterface & {
  sampleDefinitionLinks: Array<SampleDefinitionLinkNode>;
} {
  return baseIsTrialSampleInterfaceEnabled(entityOrInaccessibleInfo);
}

/**
 * Parse accessible studies to extract mapping of sampleDefinition ID to the related assays, per study.
 * @param accessibleStudies Study objects the user has access to.
 * @returns A map of study ID to a map of sampleDefinition ID to the related assays.
 */
export function extractSampleDefinitionToProceduresByStudy(accessibleStudies: Array<StudyNode>): Map<
  string, // studyId
  Map<string, Array<ProcedureDropdownTemplate>> // sampleDefinitionId to procedures
> {
  const sampleDefinitionToProceduresByStudyMap = new Map<
    string, // studyId
    Map<string, Array<ProcedureDropdownTemplate>> // sampleDefinitionId to procedures
  >();

  for (const study of accessibleStudies) {
    const studyId = study.id;
    // The mapping of sample definitions to assays for this study
    const sampleDefToProcedureMap = new Map<string, Array<ProcedureDropdownTemplate>>();
    const sampleTestPlans = accessibleObjs(study.studyDesign?.sampleTestPlans ?? []);

    for (const sampleTestPlan of sampleTestPlans) {
      const sampleDefinitionId = sampleTestPlan.sampleDefinition?.id;
      if (
        sampleDefinitionId &&
        !isInaccessible(sampleTestPlan.procedure) &&
        !sampleTestPlan.procedure.archiveData.archived
      ) {
        const proceduresForSampleDefinition = sampleDefToProcedureMap.get(sampleDefinitionId) ?? [];
        proceduresForSampleDefinition.push(sampleTestPlan.procedure);
        sampleDefToProcedureMap.set(sampleDefinitionId, proceduresForSampleDefinition);
      }
    }

    sampleDefinitionToProceduresByStudyMap.set(studyId, sampleDefToProcedureMap);
  }

  return sampleDefinitionToProceduresByStudyMap;
}

/**
 * Extract sample definition IDs per sample.
 * @param accessibleBioentities Bioentities the user has access to.
 * @returns Map of sample ID to sample definition IDs.
 */
export function extractSampleDefinitionsPerSample(
  accessibleBioentities: Array<AccessibleBioentity>
): Map<string, Array<string>> {
  // sampleId to sampleDefinitionIds
  const sampleIdToSampleDefinitionMap = new Map<string, Array<string>>();

  for (const sample of accessibleBioentities) {
    if (!isTrialSampleInterfaceEnabled(sample)) {
      sampleIdToSampleDefinitionMap.set(sample.id, []);
      continue;
    }
    const sampleDefinitionsForSample: Array<string> = [];

    for (const sampleDefinitionLink of sample.sampleDefinitionLinks) {
      const sampleDefinition = sampleDefinitionLink.sampleDefinition;
      if (sampleDefinition != null && !isInaccessible(sampleDefinition)) {
        sampleDefinitionsForSample.push(sampleDefinition.id);
      }
    }
    sampleIdToSampleDefinitionMap.set(sample.id, sampleDefinitionsForSample);
  }

  return sampleIdToSampleDefinitionMap;
}

/**
 * Given the sample definitions for a given sample, extract the related assays
 * from the sample definition to procedures map (that was populated from the study design).
 *
 * @param sampleDefinitionIds Sample definition IDs for the sample.
 * @param sampleDefToProceduresMap Map of sample definition ID to the related assays.
 */
export function extractUniqueProceduresForSample(
  sampleDefinitionIds: Array<string>,
  sampleDefToProceduresMap: Map<string, Array<ProcedureDropdownTemplate>>
): Array<ProcedureDropdownTemplate> {
  const proceduresForSample = sampleDefinitionIds.flatMap(
    (sampleDefinitionId) => sampleDefToProceduresMap.get(sampleDefinitionId) ?? []
  );

  // Deduplicate procedures for the sample
  const proceduresByProcedureId = new Map<string, ProcedureDropdownTemplate>();
  proceduresForSample.forEach((procedure) => {
    proceduresByProcedureId.set(procedure.id, procedure);
  });
  return [...proceduresByProcedureId.values()];
}

/**
 * Build a map of study ID to a map of sample ID to the related assays.
 * @param sampleIdToSampleDefinitionIdsMap Map of sample ID to sample definition IDs.
 * @param sampleDefinitionToProceduresByStudyMap Map of study ID to a map of sample definition ID to the related assays.
 * @returns A map of study ID to a map of sample ID to the related assays.
 */
export function buildStudyIdToSampleProceduresMap(
  sampleIdToSampleDefinitionIdsMap: Map<string, Array<string>>,
  sampleDefinitionToProceduresByStudyMap: Map<string, Map<string, Array<ProcedureDropdownTemplate>>>
): Map<string, Map<string, Array<ProcedureDropdownTemplate>>> {
  const studyIdToSampleProceduresMap = new Map<string, Map<string, Array<ProcedureDropdownTemplate>>>();

  for (const [studyId, sampleDefToProceduresMap] of sampleDefinitionToProceduresByStudyMap.entries()) {
    const sampleToProceduresMap = new Map<string, Array<ProcedureDropdownTemplate>>();
    for (const [sampleId, sampleDefinitionIds] of sampleIdToSampleDefinitionIdsMap.entries()) {
      const dedupedProceduresForSample = extractUniqueProceduresForSample(
        sampleDefinitionIds,
        sampleDefToProceduresMap
      );
      sampleToProceduresMap.set(sampleId, dedupedProceduresForSample);
    }
    studyIdToSampleProceduresMap.set(studyId, sampleToProceduresMap);
  }

  return studyIdToSampleProceduresMap;
}
